import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { I18nextProvider } from "react-i18next";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import AppLayout from "./components/AppLayout";
import { CartProvider } from "./contexts/CartContext";
import {
  EliteReservationContextProvider,
  EliteStructureContextProvider,
} from "./contexts/EliteTemporaryContextProvider";
import { GuestAuthProvider } from "./contexts/GuestAuthContext";
import i18n from "./i18n";
import "./index.css";
import Kiosk from "./pages/Kiosk";
import NotFound from "./pages/NotFound";
import { routes } from "./routes";

import nProgress from "nprogress";
import SurveyPage from "./pages/Survey";
import PreventiveDetail from "./pages/PreventiveDetail";
import CheckoutPage from "./pages/CheckoutPage";
import { ThemeProvider } from "./providers/ThemeProvider";
import { useEliteTheme } from "./hooks/useEliteTheme";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { environment } from "./config/env";
import PreventiveSuccess from "./pages/PreventiveSuccess";
import PreventiveRequest from "./pages/PreventiveRequest";
import PreventiveConfirmation from "./pages/PreventiveConfirmation";

function AnimatedRoutes() {
  useEliteTheme();
  const location = useLocation();
  nProgress.configure({
    showSpinner: false,
    parent: "#root",
  });
  useEffect(() => {
    nProgress.start();
    nProgress.done();
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/:structureId/kiosk" element={<Kiosk />} />
        <Route path="/survey/:accessToken" element={<SurveyPage />} />
        <Route
          path="/preventive-detail/:token"
          element={
            <ThemeProvider>
              <PreventiveDetail />
            </ThemeProvider>
          }
        />
        <Route
          path="/preventive-confirmation/:token"
          element={
            <ThemeProvider>
              <PreventiveConfirmation />
            </ThemeProvider>
          }
        />
        <Route
          path="/checkout/:token"
          element={
            <ThemeProvider>
              <CheckoutPage />
            </ThemeProvider>
          }
        />
        <Route path="/confirm-success" element={<CheckoutSuccess />} />
        <Route
          path="/confirm-success/:accessToken"
          element={<CheckoutSuccess />}
        />
        <Route path="/confirm-preventive" element={<PreventiveSuccess />} />
        <Route
          path="/preventive-request/:id"
          element={
            <ThemeProvider>
              <PreventiveRequest />
            </ThemeProvider>
          }
        />

        {/* Rotte con Layout */}
        <Route
          path="/*"
          element={
            <AppLayout>
              <PageWrapper>
                <Routes location={location} key={location.pathname}>
                  {routes
                    .filter((r) => !r.path.includes("/kiosk"))
                    .map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={route.element}
                      />
                    ))}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </PageWrapper>
            </AppLayout>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

const PageWrapper = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.2 }}
  >
    {children}
  </motion.div>
);

export function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <GoogleReCaptchaProvider reCaptchaKey={environment.RECAPTCHA_KEY}>
        <EliteStructureContextProvider>
          <EliteReservationContextProvider>
            <GuestAuthProvider>
              <CartProvider>
                <BrowserRouter basename="/">
                  <AnimatedRoutes />
                </BrowserRouter>
                <Toaster richColors />
              </CartProvider>
            </GuestAuthProvider>
          </EliteReservationContextProvider>
        </EliteStructureContextProvider>
      </GoogleReCaptchaProvider>
    </I18nextProvider>
  );
}
export default App;
